import TheRWSService from "@rws-framework/client/src/services/_service";
import { HTTPClient } from "src/services/httpClient";
import { Cache } from "src/helpers/cache";
import {
  type MediaLibraryFile,
  type MediaLibraryImage,
} from "./types-backend";
import { sortBy } from "src/helpers/utils";
import {
  Caption,
  extractFileExtension,
  UploadedFile,
  type UploadedFileOfType,
} from "./types";
import {
  convertSubtitle,
  detectSubtitleFormat,
  validateSubtitleFormat,
  type SubtitleFormat,
} from "./SubtitleConverter";

/**
 * Thin wrapper around FileUploaderLibraryService,
 * to be passed around components instead of passing multiple callbacks.
 * (simplifies mocking in storybook too)
 */
export interface LibraryApi {
  requestLibraryFiles: (ext: string[]) => Promise<MediaLibraryFile[]>;
  requestLibraryImages: () => Promise<MediaLibraryImage[]>;
  updateLibraryFile: (
    item: MediaLibraryFile,
    file: UploadedFileOfType<
      "audio" | "video" | "document" | "pdf" | "presentation" | "spreadsheet"
    >["file"],
  ) => Promise<unknown>;
  updateLibraryImage: (
    item: MediaLibraryImage,
    file: UploadedFileOfType<"image">["file"],
  ) => Promise<unknown>;
  deleteLibraryFile: (item: MediaLibraryFile) => Promise<unknown>;
  deleteLibraryImage: (item: MediaLibraryImage) => Promise<unknown>;
}

/**
 * Media library (db.files and db.images) API client.
 */
class FileUploaderLibraryService extends TheRWSService {
  static _IN_CLIENT: boolean = true;

  private cache = new Cache();
  private client = new HTTPClient({});

  requestLibraryFiles = this.cache.cached<FileUploaderLibraryService['requestLibraryFilesUncached']>(
    (courseId, ext) => `files-${courseId}-${ext.join(",")}`,
    (courseId, ext) => this.requestLibraryFilesUncached(courseId, ext),
  );
  clearFileCache = () => this.cache.clearByPrefix("files-");
  private requestLibraryFilesUncached(
    courseId: string,
    ext: string[],
  ): Promise<MediaLibraryFile[]> {
    // files:get
    return this.client
      .get(`/files/${courseId}/`, { search: { ext: ext.join(",") } })
      .then((data) =>
        sortBy<MediaLibraryFile>(data.files, (i) => i.created_at).reverse(),
      );
  }

  requestLibraryImages = this.cache.cached<FileUploaderLibraryService['requestLibraryImagesUncached']>(
    (courseId) => `images-${courseId}`,
    (courseId) => this.requestLibraryImagesUncached(courseId),
  );
  clearImageCache = () => this.cache.clearByPrefix("images-");
  private requestLibraryImagesUncached(
    courseId: string,
  ): Promise<MediaLibraryImage[]> {
    // get_images
    return this.client
      .get(`/images/${courseId}/`, { search: { ext: "img" } })
      .then((data) =>
        sortBy<MediaLibraryImage>(data.images, (i) => i.created_at).reverse(),
      );
  }

  updateLibraryFile(
    item: Partial<MediaLibraryFile>,
    file: UploadedFileOfType<
      "audio" | "video" | "document" | "pdf" | "presentation" | "spreadsheet"
    >["file"],
  ): Promise<unknown> {
    const body: Partial<MediaLibraryFile> = {
      name: `${file.name}${file.ext}`,

      ...((file.type === "video" || file.type === "audio") && {
        description_array: file.description
          .map(FileUploaderLibraryService.convertCaptionToVtt)
          .map(Caption.serializeToString)
          .filter(Boolean),
        subtitles_array: file.subtitles
          .map(FileUploaderLibraryService.convertCaptionToVtt)
          .map(Caption.serializeToString)
          .filter(Boolean),
      }),

      ...(file.type === "video" && file.audioDescription && {
        audio_description: file.audioDescription,
      }),
    };

    // media:update
    return this.client
      .put(`/media/files/${item._id}/`, { body })
      .then((data) => {
        this.clearFileCache();
        return data;
      });
  }

  updateLibraryImage(
    item: Partial<MediaLibraryImage>,
    file: UploadedFileOfType<"image">["file"],
  ): Promise<unknown> {
    const body = {
      caption: file.caption || "",
      alt_text: file.alt,
      name: `${file.name}${file.ext}`,
    };

    // media:update
    return this.client
      .put(`/media/images/${item._id}/`, { body })
      .then((data) => {
        this.clearImageCache();
        return data;
      });
  }

  deleteLibraryFile(
    courseId: string,
    item: MediaLibraryFile,
  ): Promise<unknown> {
    // media:delete
    return this.client
      .delete(`/media/files/${courseId}/${item._id}/`)
      .then((data) => {
        this.cache.clearByPrefix("files-");
        return data;
      });
  }

  deleteLibraryImage(
    courseId: string,
    item: MediaLibraryImage,
  ): Promise<unknown> {
    // media:delete
    return this.client
      .delete(`/media/images/${courseId}/${item._id}/`)
      .then((data) => {
        this.cache.clearByPrefix("images-");
        return data;
      });
  }

  clearCacheBasedOnUpload(upload: UploadedFile) {
    return upload.file.type === "image"
      ? this.clearImageCache()
      : this.clearFileCache();
  }

  convertLegacyCaptionsIfNeeded(upload: UploadedFile): Promise<unknown> {
    const needsConversion =
      upload.source.type === "library" &&
      (upload.file.type === "video" || upload.file.type === "audio") &&
      (upload.file.subtitles || [])
        .concat(upload.file.description || [])
        .some(({ dataUrl }) => dataUrl.includes("text/x-ssa"));

    if (upload.source.type !== "library" || !needsConversion)
      return Promise.resolve({});

    return this.updateLibraryFile(
      { _id: upload.source.id },
      upload.file as UploadedFileOfType<"audio" | "video">["file"],
    );
  }

  /**
   * Converts subtitles/descriptions to text/vtt format.
   *
   * For subtitles uploaded as files:
   * .vtt: (no conversion, store as `data:text/vtt;base64,${btoa(encodeToUTF8(vtt))}`)
   * .srt: application/x-subrip -> text/vtt (store as `data:text/vtt;base64,${btoa(encodeToUTF8(vtt))}`)
   *
   * For existing base64 subtitles in legacy format:
   * text/x-ssa -> text/vtt
   * (`data:text/x-ssa;base64,<DATA_URL>` -> data:text/vtt;base64,<DATA_URL>)
   */
  static convertCaptionToVtt(caption: Caption): Caption {
    // data:[<media-type>][;base64],<data>
    const match = caption.dataUrl.match(/^data:([^;]+);base64,(.+)$/);
    const { ext } = extractFileExtension(caption.name);

    if (!match) return caption;

    const [_, type, data] = match;
    const text = FileUploaderLibraryService.decodeUTF8String(atob(data));

    const extensionToType: Record<string, SubtitleFormat> = {
      srt: "application/x-subrip",
      ssa: "text/x-ssa",
    };

    // browser-reported mime type and file extension cannot be trusted, so detect
    // format from file contents first, falling back to type/ext
    const from: SubtitleFormat | undefined =
      detectSubtitleFormat(text) ??
      (validateSubtitleFormat(type) ? type : extensionToType[ext.replace(".", "")]);

    if (from === "text/vtt") return {
      ...caption,
      // `.vtt` files uploaded from disk may have application/octet-stream type
      dataUrl: `data:text/vtt;base64,${data}`,
    };

    if (!from) console.error(`Unknown subtitle format`, caption);

    const { text: vtt, error } = convertSubtitle(text, {
      from,
      to: "text/vtt",
    });

    if (error) console.error(error);

    return {
      ...caption,
      dataUrl: `data:text/vtt;base64,${btoa(FileUploaderLibraryService.encodeToUTF8(vtt))}`,
    };
  }

  asApi(courseId: string): LibraryApi {
    return {
      requestLibraryFiles: (ext) => this.requestLibraryFiles(courseId, ext),
      requestLibraryImages: () => this.requestLibraryImages(courseId),
      updateLibraryFile: (item, file) => this.updateLibraryFile(item, file),
      updateLibraryImage: (item, file) => this.updateLibraryImage(item, file),
      deleteLibraryFile: (item) => this.deleteLibraryFile(courseId, item),
      deleteLibraryImage: (item) => this.deleteLibraryImage(courseId, item),
    };
  }

  static STRING_UTF8_DECODER = new TextDecoder('utf-8');
  static decodeUTF8String(str: string) {
    return FileUploaderLibraryService.STRING_UTF8_DECODER.decode(
      Uint8Array.from(str, (c) => c.charCodeAt(0)),
    );
  }

  static STRING_UTF8_ENCODER = new TextEncoder();
  static encodeToUTF8(str: string) {
    const bytes = FileUploaderLibraryService.STRING_UTF8_ENCODER.encode(str);
    return String.fromCharCode(...bytes);
  }
}

export default FileUploaderLibraryService.getSingleton();
export { FileUploaderLibraryService as FileUploaderLibraryServiceInstance };
